import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import './dashboard.css';
import LeaseApiService from '../../services/apiService';

const apiService = new LeaseApiService();

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BAR_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#3B82F6', '#A855F7', '#FB923C', '#14B8A6', '#EF4444', '#84CC16'];

// Icon components (kept consistent with the existing dashboard visual language)
const UsersIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const BuildingIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M3 21h18"></path>
    <path d="M5 21V7l8-4v18"></path>
    <path d="M19 21V11l-6-4"></path>
    <rect x="9" y="9" width="4" height="4"></rect>
    <rect x="9" y="14" width="4" height="4"></rect>
    <rect x="14" y="14" width="4" height="4"></rect>
  </svg>
);

const HomeIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const CheckIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const KeyIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
  </svg>
);

const LockIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="4" y="10" width="16" height="10" rx="2"></rect>
    <path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
  </svg>
);

const ChartIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);

const RefreshIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

// Routes for card navigation
const ROUTES = {
  customer: '/Customer/Property/Master/Customer/Listing',
  property: '/Customer/Property/Master/Property/Listing',
  unit: '/Customer/Property/Master/Unit/Listing',
  unitBlock: '/Customer/Property/Master/UnitBlock/Listing',
};

// Defensive multi-casing readers - backend field casing has been inconsistent
// across these endpoints elsewhere in this codebase (see unitBlock/listing.js).
const getUnitProperty = (row) => row?.property_Name || row?.propertyName || row?.Property_Name || 'Unknown';
const getUnitStatus = (row) => row?.status || row?.unitStatus || row?.Status || 'Unknown';
const getBlockingProperty = (row) => row?.propertyName || row?.property_Name || row?.PropertyName || 'Unknown';
const getBlockingStatus = (row) => row?.status || row?.unitStatus || row?.Status || 'Unknown';

const buildLast12MonthsBuckets = (rows, dateField) => {
  const buckets = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      month: `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`,
      count: 0,
    });
  }
  rows.forEach((row) => {
    const raw = row?.[dateField];
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const bucket = buckets.find((b) => b.key === key);
    if (bucket) bucket.count += 1;
  });
  return buckets;
};

const groupByAccessor = (rows, accessor, limit) => {
  const counts = {};
  rows.forEach((row) => {
    const key = accessor(row) || 'Unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  const arr = Object.entries(counts).map(([name, count]) => ({ name, count }));
  arr.sort((a, b) => b.count - a.count);
  return limit ? arr.slice(0, limit) : arr;
};

const tooltipStyle = {
  backgroundColor: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '6px',
  fontSize: '12px',
};

const BarWidget = ({ title, iconBg, icon, data, barColor, useMultiColor, height = 300, angledLabels }) => (
  <div className="widget-card">
    <div className="widget-title">
      {title}
      <div className="icon-badge" style={{ backgroundColor: iconBg }}>
        {icon}
      </div>
    </div>
    {data && data.length > 0 ? (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: angledLabels ? 50 : 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#E5E7EB' }}
            interval={0}
            angle={angledLabels ? -30 : 0}
            textAnchor={angledLabels ? 'end' : 'middle'}
            height={angledLabels ? 60 : 30}
          />
          <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} allowDecimals={false} width={35} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} animationDuration={800}>
            <LabelList dataKey="count" position="top" style={{ fontSize: '10px', fill: '#6B7280' }} />
            {useMultiColor
              ? data.map((entry, index) => <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />)
              : data.map((entry, index) => <Cell key={`cell-${index}`} fill={barColor} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    ) : (
      <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>No data available</div>
    )}
  </div>
);

const CustomerPropertyDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customerCount, setCustomerCount] = useState(0);
  const [propertyCount, setPropertyCount] = useState(0);
  const [unitRows, setUnitRows] = useState([]);
  const [unitTotal, setUnitTotal] = useState(0);
  const [blockingRows, setBlockingRows] = useState([]);
  const [customerMonthlyData, setCustomerMonthlyData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    await Promise.all([fetchCustomers(), fetchProperties(), fetchUnits(), fetchUnitBlockings()]);
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      params.append('FullName', '');
      const res = await apiService.getApiParamater('GET_CUSTOMER', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setCustomerCount(res?.data?.paginationData?.totalRecords ?? rows.length);
      setCustomerMonthlyData(buildLast12MonthsBuckets(rows, 'createdOn'));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 1);
      params.append('Community', '');
      params.append('PropertyName', '');
      const res = await apiService.getApiParamater('GET_PROPERTY_LIST', `?${params.toString()}`);
      setPropertyCount(res?.data?.paginationData?.totalRecords ?? (res?.data?.result || []).length);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      const res = await apiService.getApiParamater('GET_UNIT_LIST', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setUnitRows(rows);
      setUnitTotal(res?.data?.paginationData?.totalRecords ?? rows.length);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchUnitBlockings = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      params.append('PropertyNo', '');
      params.append('UnitNo', '');
      params.append('Status', '');
      const res = await apiService.getApiParamater('GET_ALL_UNIT_BLOCKINGS', `?${params.toString()}`);
      setBlockingRows(res?.data?.result || []);
    } catch (error) {
      console.error('Error fetching unit blockings:', error);
    }
  };

  const isStatus = (row, matches) => matches.includes(getUnitStatus(row).toLowerCase());
  const availableCount = unitRows.filter((r) => isStatus(r, ['available'])).length;

  const kpiCards = [
    {
      id: 'customer',
      value: customerCount,
      label: 'Total Customers',
      color: '#E0E7FF',
      iconColor: '#6366F1',
      icon: UsersIcon,
      route: ROUTES.customer,
    },
    {
      id: 'property',
      value: propertyCount,
      label: 'Total Properties',
      color: '#FFEDD5',
      iconColor: '#FB923C',
      icon: BuildingIcon,
      route: ROUTES.property,
    },
    {
      id: 'unit',
      value: unitTotal,
      label: 'Total Units',
      color: '#D1FAE5',
      iconColor: '#10B981',
      icon: HomeIcon,
      route: ROUTES.unit,
    },
    {
      id: 'available',
      value: availableCount,
      label: 'Available Units',
      color: '#E5FFE5',
      iconColor: '#22C55E',
      icon: CheckIcon,
      route: ROUTES.unit,
    },
    {
      id: 'blocked',
      value: blockingRows.length,
      label: 'Blocked Units',
      color: '#FFE5E5',
      iconColor: '#EF4444',
      icon: LockIcon,
      route: ROUTES.unitBlock,
    },
  ];

  const unitsByProperty = groupByAccessor(unitRows, getUnitProperty, 10);
  const unitsByStatus = groupByAccessor(unitRows, getUnitStatus);
  const blockingByProperty = groupByAccessor(blockingRows, getBlockingProperty, 10);
  const blockingByStatus = groupByAccessor(blockingRows, getBlockingStatus);

  const handleCardClick = (route) => {
    if (route) navigate(route);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading dashboard data...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '20px' }}>
        <h5 className="mb-0">Customer Dashboard</h5>
        <div
          onClick={fetchDashboardData}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            backgroundColor: 'white',
            padding: '8px 16px',
            borderRadius: '10px',
            border: '1px solid #E5E7EB',
            fontSize: '13px',
            color: '#6B7280',
          }}
        >
          <RefreshIcon color="#6B7280" />
          Refresh
        </div>
      </div>

      {/* KPI Cards */}
      <div className="main-card-container">
        <div className="stats-grid2">
          {kpiCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                onClick={() => handleCardClick(card.route)}
                className="stat-card"
                style={{ backgroundColor: card.color, borderLeftColor: card.iconColor }}
              >
                <div className="stat-header">
                  <div className="stat-icon-wrapper" style={{ backgroundColor: card.iconColor }}>
                    <Icon color="#fff" />
                  </div>
                  <div className="stat-label">{card.label}</div>
                </div>
                <div className="stat-value" style={{ color: card.iconColor }}>
                  {card.value.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Customers by Month */}
      <div className="widget-card" style={{ marginBottom: '20px' }}>
        <div className="widget-title">
          Customers by Month (Last 12)
          <div className="icon-badge" style={{ backgroundColor: '#EEF2FF' }}>
            <UsersIcon color="#6366F1" />
          </div>
        </div>
        {customerMonthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={customerMonthlyData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} allowDecimals={false} width={35} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone"
                dataKey="count"
                name="Customers"
                stroke="#38BDF8"
                strokeWidth={2}
                dot={{ stroke: '#F87171', strokeWidth: 2, r: 4, fill: '#fff' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>No data available</div>
        )}
      </div>

      {/* Units by Property / Units by Status */}
      <div className="widgets-grid-2">
        <BarWidget
          title="Units by Property (Top 10)"
          iconBg="#D1FAE5"
          icon={<BuildingIcon color="#10B981" />}
          data={unitsByProperty}
          useMultiColor
          angledLabels
        />
        <BarWidget title="Units by Status" iconBg="#FEF3C7" icon={<KeyIcon color="#F59E0B" />} data={unitsByStatus} useMultiColor />
      </div>

      {/* Unit Blocking Breakdown */}
      <div className="widgets-grid-2">
        <BarWidget
          title="Blocked Units by Property (Top 10)"
          iconBg="#FFE5E5"
          icon={<LockIcon color="#EF4444" />}
          data={blockingByProperty}
          barColor="#EF4444"
          angledLabels
        />
        <BarWidget title="Unit Blocking by Status" iconBg="#F3E8FF" icon={<ChartIcon color="#A855F7" />} data={blockingByStatus} useMultiColor />
      </div>
    </div>
  );
};

export default CustomerPropertyDashboard;
